import React, { useCallback, useEffect, useState } from "react";
import { isEmpty } from "lodash";

import {
    calcEla,
    airtightnessTypeValidation,
    ach50Validation,
    eqLeakageAreaValidation,
    testPressureValidation,
    siteTerrainValidation,
    weatherTerrainValidation,
    depresStatusValidation,
    depresResultValidation,
    localShieldingFlueValidation,
    localShieldingWallsValidation,
    anemometerValidation,
    ceilingLeakageValidation,
    wallsLeakageValidation,
    floorsLeakageValidation,
    windowTightnessValidation,
    commonFloorsValidation,
    commonWallsValidation,
    commonCeilingsValidation,
    commonTotalValidation,
} from "utils/airtightness";
import { useStateSelector } from "utils/field";
import { getBaseUnits, getOptions, getUnitOptions } from "utils/fields";
import { getDecimalPlaces } from "utils/fieldValidation";
import { calcBuildingSurfaceArea, getAttachmentType } from "utils/enclosure/geometry";
import airTestDataTemplate from "./templates/airTestData.json";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Checkbox from "components/Input/Checkbox";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import Button from "components/Button";
import TabHeader from "components/TabHeader";
import DetailsDrawer from "components/DetailsDrawer";
import Details from "./Details";
import TestData from "./TestData";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import NonH2kParam from "assets/images/icons/JSX/NonH2kParam";

const Airtightness = ({
    history,
    accessor,
    change,
    isParametricAnalysis = false,
    isUpgrade = false,
    isUpgradeLibrary = false,
    isCommunity = false,
    canBeEditedFromParametric = true,
}) => {
    const [open, toggleOpen] = useState(false);

    const accessorForModelDataFromParametric = `parametricAnalysis.parametricAnalysis.model.${accessor}`;

    const { contentOpen = false } = useStateSelector(`model`) || {};
    const { modelData: modelDataFromParametric = {} } =
        useStateSelector(`parametricAnalysis.parametricAnalysis.model`) || {};

    const { id: airTightnessTypeId } = useStateSelector(`${accessor}.testType`) || {};
    const achValue = useStateSelector(`${accessor}.blowerTest.airChangeRate50Pa`);
    const { id: pressureId } = useStateSelector(`${accessor}.blowerTest.pressure`) || {};
    const eqLeakageValue = useStateSelector(`${accessor}.blowerTest.eqLeakageArea`) || 0;
    const eqLeakageUnits = useStateSelector(`${accessor}.blowerTest.eqLeakageArea_u`);
    const exhaustDeviceTestUnits = useStateSelector(`${accessor}.exhaustDevicesTest_u`);
    const anemometerUnits = useStateSelector(`${accessor}.other.anemometerHeight_u`);
    const isEqLeakageAreaCalculated = useStateSelector(`${accessor}.blowerTest.isEqLeakageAreaCalculated`);
    const { id: exhaustTestId, result: exhaustTestResult } = useStateSelector(`${accessor}.exhaustDevicesTest`) || {};
    const isDefaultLeakage = useStateSelector(`${accessor}.other.leakageFractions.useDefaults`);
    const commonFloorsValue = useStateSelector(`${accessor}.commonSurfaces.floors`) || 0;
    const commonWallsValue = useStateSelector(`${accessor}.commonSurfaces.walls`) || 0;
    const commonCeilingsValue = useStateSelector(`${accessor}.commonSurfaces.ceilings`) || 0;
    const commonFloorsUnits = useStateSelector(`${accessor}.commonSurfaces.floors_u`);
    const commonWallsUnits = useStateSelector(`${accessor}.commonSurfaces.walls_u`);
    const commonCeilingsUnits = useStateSelector(`${accessor}.commonSurfaces.ceilings_u`);
    const commonTotalUnits = useStateSelector(`${accessor}.commonSurfaces.total_u`);
    const buildingSurfaceAreaUnits = useStateSelector(`${accessor}.buildingSurfaceArea_u`);
    const buildingSurfaceAreaOverrideEnabled = useStateSelector(`${accessor}.buildingSurfaceAreaOverride.enabled`);
    const buildingSurfaceAreaOverrideTotalArea = useStateSelector(`${accessor}.buildingSurfaceAreaOverride.totalArea`);
    const buildingSurfaceAreaOverrideTotalAreaUnits = useStateSelector(
        `${accessor}.buildingSurfaceAreaOverride.totalArea_u`
    );

    const normalizedAirLeakageRateValue = useStateSelector(`${accessor}.normalizedAirLeakageRate`);
    const normalizedAirLeakageRateUnits = useStateSelector(`${accessor}.normalizedAirLeakageRate_u`);
    const normalizedLeakageAreaUnits = useStateSelector(`${accessor}.normalizedLeakageArea_u`);

    const { id: windowTightnessId, value: windowTightnessValue } = isParametricAnalysis
        ? modelDataFromParametric?.specifications?.windowTightness
        : useStateSelector(`model.modelData.specifications.windowTightness`) || {};

    const { id: buildingTypeId } = isParametricAnalysis
        ? modelDataFromParametric?.specifications?.buildingType
        : useStateSelector(`model.modelData.specifications.buildingType`) || {};

    const { id: houseTypeId } = isParametricAnalysis
        ? modelDataFromParametric?.specifications?.buildingSubType
        : useStateSelector(`model.modelData.specifications.buildingSubType`) || {};

    const modelUnits = isParametricAnalysis
        ? modelDataFromParametric?.uiSettings?.primaryUnits
        : useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const houseVolume =
        isUpgradeLibrary || isCommunity
            ? 600
            : isParametricAnalysis
              ? modelDataFromParametric?.dimensions?.volume?.total
              : useStateSelector(`model.modelData.dimensions.volume.total`) || 0;

    const components = isParametricAnalysis
        ? modelDataFromParametric?.components
        : useStateSelector(`model.modelData.components`) || {};

    const uploadedData = isParametricAnalysis
        ? modelDataFromParametric[accessor]?.uploadedData
        : useStateSelector(`${accessor}.uploadedData`) || {
              data: "",
          };

    const airtightnessInputType = useStateSelector(`${accessor}.airtightnessInputType`) || "";

    const isMURB = buildingTypeId !== 0;
    const totalCommonAreaValue = commonFloorsValue + commonWallsValue + commonCeilingsValue || 0;

    const isDetached = getAttachmentType(buildingTypeId, houseTypeId) === "detached";

    let { major: h2kMajor, minor: h2kMinor } =
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.h2kVersion`
                : `model.modelData.h2kVersion`
        ) || {};

    if (isCommunity) {
        h2kMinor = 12;
    }

    let isAirTestEnabled = useStateSelector(
        isParametricAnalysis
            ? `parametricAnalysis.parametricAnalysis.model.modelData.airTightness.testDataEnabled`
            : `model.modelData.airTightness.testDataEnabled`
    );

    const baseFileHasAirTestData = isAirTestEnabled;

    //If we're outside of the model, force this to be FALSE
    const isNotInBaseModel = isParametricAnalysis || isUpgrade || isUpgradeLibrary || isCommunity;
    if (isNotInBaseModel) {
        isAirTestEnabled = false;
    }

    const testData =
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.airTightness.testData`
                : `model.modelData.airTightness.testData`
        ) || {};

    //ACH = Q@∆P / volume => [L/s] or [cfm]
    const airflow = achValue * houseVolume * 1000 * (1 / 3600); //[houses/h] * [m3/house] * [1000 L/1m3] * [1h/3600s]

    //NLR = Q@∆P / SA => [L/s/m2]
    const componentSurfaceArea = calcBuildingSurfaceArea(components, false);
    const buildingSurfaceArea = buildingSurfaceAreaOverrideEnabled
        ? buildingSurfaceAreaOverrideTotalArea
        : totalCommonAreaValue + componentSurfaceArea;

    const normalizedAirLeakageRate = airflow / buildingSurfaceArea;

    const achFromNlr = parseFloat(
        ((normalizedAirLeakageRateValue * buildingSurfaceArea) / (houseVolume * 1000 * (1 / 3600))).toFixed(2)
    );

    // Normalized Leakage Area (NLA) = ELA / SA
    // Note that ELA is already at 10Pa
    // const assumedFlowExponent = 0.65;
    const normalizedLeakageArea = eqLeakageValue / buildingSurfaceArea;

    const airTestDataEmpty = isEmpty(testData);

    //Calculate ELA upper and lower bounds;
    const lowerFlowExponent = 0.5;
    const upperFlowExponent = 1;

    const lowerFlowCoefficient = airflow / 50 ** lowerFlowExponent;
    const upperFlowCoefficient = airflow / 50 ** upperFlowExponent;

    const upperELA = 11.57 * Math.sqrt(1.205) * lowerFlowCoefficient * 10 ** (lowerFlowExponent - 0.5); //produces a higher ELA value
    const lowerELA = 11.57 * Math.sqrt(1.205) * upperFlowCoefficient * 10 ** (upperFlowExponent - 0.5); //produces a lower ELA value

    const elaWarningRange = `${lowerELA.toFixed(1)} to ${upperELA.toFixed(1)}`;
    const isElaOutOfBounds = eqLeakageValue < lowerELA || eqLeakageValue > upperELA;

    const handleAchTypeChange = useCallback(
        ({ id }) => {
            const airtightnessValue = {
                0: achValue,
                1: 10.35,
                2: 4.55,
                3: 3.57,
                4: 1.5,
            }[id];
            change(`${accessor}.blowerTest.isCgsbTest`, id !== 0);
            change(`${accessor}.blowerTest.airChangeRate50Pa`, airtightnessValue);

            if (houseVolume !== 0 && airtightnessValue !== 0 && isEqLeakageAreaCalculated) {
                change(`${accessor}.blowerTest.eqLeakageArea`, calcEla(houseVolume, airtightnessValue, id));
            }
            if (id !== 0) {
                change(`${accessor}.blowerTest.pressure`, { id: 0 });
                change(`${accessor}.blowerTest.isEqLeakageAreaCalculated`, true);
            }

            change(`${accessor}.airtightnessInputType`, "ach");
        },
        [airTightnessTypeId, achValue, houseVolume]
    );

    const handleAchChange = useCallback(() => {
        if (houseVolume !== 0 && achValue !== 0 && isEqLeakageAreaCalculated) {
            change(`${accessor}.blowerTest.eqLeakageArea`, calcEla(houseVolume, achValue, airTightnessTypeId));
        }
    }, [achValue, houseVolume, isEqLeakageAreaCalculated, airTightnessTypeId]);

    //Check for fixed airtightness or blower door values
    const isBlowerTestValues = airTightnessTypeId === 0;

    useEffect(() => {
        if (exhaustTestResult == null || isNaN(exhaustTestResult)) {
            change(`${accessor}.exhaustDevicesTest.result`, 0);
        }
    }, [exhaustTestResult]);

    const handleIsEqCalculatedChange = useCallback(
        (e) => {
            if (!isEqLeakageAreaCalculated) {
                //e.target.value is the opposite of the form value for is calculated, and it's a string
                change(`${accessor}.blowerTest.eqLeakageArea`, calcEla(houseVolume, achValue, airTightnessTypeId));
            }
        },
        [houseVolume, achValue, pressureId, accessor, isEqLeakageAreaCalculated, airTightnessTypeId]
    );

    const handleTestDataEnabledToggle = useCallback(
        (e) => {
            //set all disabled fields to their appropriate values,
            //pre populate
            if (!isAirTestEnabled && airTestDataEmpty && !isUpgrade) {
                change(`model.modelData.airTightness.testData`, airTestDataTemplate);
            }
        },
        [isAirTestEnabled, airTestDataEmpty, isUpgrade]
    );

    const handleInputTypeChange = useCallback(
        (value) => {
            if (value === "nlr" && achFromNlr !== null && !isNaN(achFromNlr)) {
                change(`${accessor}.blowerTest.airChangeRate50Pa`, achFromNlr);
                change(`${accessor}.blowerTest.isEqLeakageAreaCalculated`, true);
            }
        },
        [achFromNlr, accessor]
    );

    useEffect(() => {
        // Initialize pressure value if null on first render
        if (pressureId == null) {
            change(`${accessor}.blowerTest.pressure`, { id: 0 });
        }
        // Automatically recalculate ELA value on first render to avoid wrong values when volume changes
        const calculatedELA = calcEla(houseVolume, achValue, airTightnessTypeId);
        if (eqLeakageValue !== calculatedELA && isEqLeakageAreaCalculated) {
            change(`${accessor}.blowerTest.eqLeakageArea`, calculatedELA);
        }
    }, []);

    return (
        <>
            {!isUpgrade && (
                <TabHeader
                    title="Airtightness"
                    description="Information about the building's air infiltration data."
                    detailsTrigger={() => toggleOpen(true)}
                    detailsText="Airtightness Details"
                />
            )}
            <div className={`${!isUpgrade && sharedClasses.section} ${classes.airtightness}`}>
                <DetailsDrawer open={open} close={() => toggleOpen(false)} title="Airtightness Tab Details">
                    <Details />
                    <div className={classes.buttons}>
                        <Button large type="hollow" onClick={() => toggleOpen(false)}>
                            Close
                        </Button>
                    </div>
                </DetailsDrawer>
                <h3 style={{ marginBottom: "1rem" }}>Blower Door Test</h3>
                <InputRow gridTemplate={isUpgrade ? "1fr 1fr 1fr" : "1fr 1fr 1fr 1fr"}>
                    <Field
                        component={Select}
                        name={`${accessor}.testType`}
                        options={getOptions({ fieldKey: "airTightnessType" })}
                        label="Air Tightness Type"
                        placeholder="Choose Air Tightness Type"
                        validate={airtightnessTypeValidation}
                        info="Defines whether blower door test results will be input, or whether a pre-defined ACH50 should be applied. Note that the test type is always assumed to be CGSB."
                        onChange={handleAchTypeChange}
                        disabled={!canBeEditedFromParametric}
                        warning={
                            isAirTestEnabled && airTightnessTypeId !== 0
                                ? "airLeakageDataDropdown"
                                : baseFileHasAirTestData && isNotInBaseModel && airTightnessTypeId !== 0
                                  ? "airLeakageDataInBase"
                                  : null
                        }
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.blowerTest.airChangeRate50Pa`}
                        label="Air Change Rate"
                        placeholder={0.0}
                        disabled={!isBlowerTestValues || isAirTestEnabled || !canBeEditedFromParametric}
                        validate={ach50Validation}
                        setTouched={true}
                        decimals={getDecimalPlaces("airChangeRate50Pa")}
                        change={change}
                        setValue={airtightnessInputType === "nlr" ? achFromNlr : achValue}
                        units={{
                            base: getBaseUnits("airChangeRate50Pa", modelUnits),
                            options: getUnitOptions("airtightness"),
                            selected: "ACH50",
                            unitType: "airtightness",
                            accessor: `${accessor}.blowerTest.airChangeRate50Pa_u`,
                        }}
                        onChange={handleAchChange}
                        info={
                            isAirTestEnabled
                                ? "When air leakage test data is provided, this value will be calculated and updated every time a HOT2000 simulation is run."
                                : null
                        }
                    />
                    <Field
                        component={Select}
                        name={`${accessor}.blowerTest.isCgsbTest`}
                        options={[
                            {
                                label: "CGSB Test",
                                value: true,
                            },
                            {
                                label: "As operated",
                                value: false,
                            },
                        ]}
                        label="Test Type"
                        placeholder="Choose Test Type"
                        disabled={airTightnessTypeId !== 0 || isAirTestEnabled || !canBeEditedFromParametric}
                        info={isAirTestEnabled && "Controlled by air leakage test data."}
                    />
                    {!isUpgrade && (
                        // <div className={classes.altdCheck}>
                        <Field
                            component={Checkbox}
                            name={`model.modelData.airTightness.testDataEnabled`}
                            label="Air Leakage Test Data"
                            large
                            className={classes.bottomCheckBox}
                            type="checkbox"
                            disabled={airTightnessTypeId !== 0 || !canBeEditedFromParametric}
                            onChange={handleTestDataEnabledToggle}
                        />
                        // </div>
                    )}
                </InputRow>
                <InputRow gridTemplate={isUpgrade ? "1fr 1fr 1fr" : "1fr 1fr 1fr 1fr"}>
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.blowerTest.eqLeakageArea`}
                        label="Equivalent Leakage Area"
                        placeholder={0.0}
                        disabled={
                            isEqLeakageAreaCalculated ||
                            !isBlowerTestValues ||
                            isAirTestEnabled ||
                            !canBeEditedFromParametric
                        }
                        validate={eqLeakageAreaValidation}
                        setValue={eqLeakageValue}
                        setTouched={true}
                        decimals={getDecimalPlaces("eqLeakageArea")}
                        change={change}
                        units={{
                            base: getBaseUnits("eqLeakageArea", modelUnits),
                            options: getUnitOptions("area"),
                            selected: eqLeakageUnits,
                            unitType: "area",
                            accessor: `${accessor}.blowerTest.eqLeakageArea_u`,
                        }}
                        info={
                            isUpgradeLibrary || isCommunity
                                ? "Placeholder volume (21,000 ft3) used for ELA calculation. The value will be updated for each model this upgrade is applied to."
                                : isAirTestEnabled
                                  ? "When air leakage test data is provided, this value will be calculated and updated every time a HOT2000 simulation is run."
                                  : null
                        }
                        warning={isElaOutOfBounds ? `elaOutOfBounds-${elaWarningRange}` : null}
                    />
                    <Field
                        component={Select}
                        name={`${accessor}.blowerTest.pressure`}
                        options={getOptions({ fieldKey: "blowerTestPressure" })}
                        label="Equivalent Leakage Area Pressure"
                        placeholder="Choose Pressure"
                        validate={testPressureValidation}
                        disabled={
                            isEqLeakageAreaCalculated ||
                            !isBlowerTestValues ||
                            isAirTestEnabled ||
                            !canBeEditedFromParametric
                        }
                    />
                    <Field
                        component={Checkbox}
                        name={`${accessor}.blowerTest.isEqLeakageAreaCalculated`}
                        label="Calculate Equivalent Leakage Area"
                        large
                        className={classes.bottomCheckBox}
                        type="checkbox"
                        onChange={handleIsEqCalculatedChange}
                        disabled={
                            isAirTestEnabled || airtightnessInputType === "nlr" || isCommunity || isParametricAnalysis
                        }
                    />
                </InputRow>
                {h2kMinor == 10 && (
                    <InputRow gridTemplate="1fr 6fr">
                        <Field
                            component={Checkbox}
                            name={`${accessor}.blowerTest.unguarded`}
                            label="Is Unguarded"
                            large
                            className={classes.bottomCheckBox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                )}
                {h2kMinor >= 11 && (
                    <InputRow gridTemplate="1fr 6fr">
                        <Field
                            component={Checkbox}
                            name={`${accessor}.blowerTest.guarded`}
                            label="Is Guarded"
                            large
                            className={classes.bottomCheckBox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                )}
                <InputRow gridTemplate="1fr 1fr 1fr 1fr" style={{ alignItems: "flex-end", marginTop: "1.5rem" }}>
                    <Field
                        component={Select}
                        name={`${accessor}.airtightnessInputType`}
                        options={[
                            {
                                label: "Default (ACH)",
                                value: "ach",
                            },
                            {
                                label: "NLR Target",
                                value: "nlr",
                            },
                        ]}
                        label="Airtightness Input Type"
                        placeholder="Default (ACH)"
                        disabled={airTightnessTypeId !== 0 || isAirTestEnabled || !canBeEditedFromParametric}
                        info={
                            "Use this field to specify an NLR target and calculate the resulting ACH. Ensure ELA is also calculated. HOT2000 will only evaluate the file based on the resulting ACH and ELA value."
                        }
                        onChange={handleInputTypeChange}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.normalizedAirLeakageRate`}
                        label="NLR"
                        placeholder={0.0}
                        disabled={
                            airtightnessInputType !== "nlr" ||
                            airTightnessTypeId !== 0 ||
                            isAirTestEnabled ||
                            !canBeEditedFromParametric
                        }
                        decimals={2}
                        change={change}
                        setValue={airtightnessInputType !== "nlr" ? normalizedAirLeakageRate : null}
                        units={{
                            base: getBaseUnits("normalizedAirLeakageRate", modelUnits),
                            options: getUnitOptions("normAirLeakage"),
                            selected: normalizedAirLeakageRateUnits,
                            unitType: "normAirLeakage",
                            accessor: `${accessor}.normalizedAirLeakageRate_u`,
                        }}
                        info={"Normalized Leakage Rate (NLR @50 Pa)"}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.normalizedLeakageArea`}
                        label="NLA"
                        placeholder={0.0}
                        disabled={true}
                        decimals={2}
                        change={change}
                        setValue={normalizedLeakageArea}
                        units={{
                            base: getBaseUnits("normalizedLeakageArea", modelUnits),
                            options: getUnitOptions("normLeakageArea"),
                            selected: normalizedLeakageAreaUnits,
                            unitType: "normLeakageArea",
                            accessor: `${accessor}.normalizedLeakageArea_u`,
                        }}
                        info="Normalized Leakage Area. Based on the Equivalent Leakage Area Pressure entered in the field above (default 10Pa)."
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.buildingSurfaceArea`}
                        label="Building Enclosure Area"
                        placeholder={0.0}
                        disabled={true}
                        decimals={2}
                        change={change}
                        setValue={buildingSurfaceArea}
                        units={{
                            base: getBaseUnits("commonAreaWalls", modelUnits),
                            options: ["m2", "ft2"],
                            selected: buildingSurfaceAreaUnits,
                            unitType: "area",
                            accessor: `${accessor}.buildingSurfaceArea_u`,
                        }}
                        info="Based on HOT2000 component areas plus common areas defined in MURB Specifications below, if applicable."
                    />
                </InputRow>
                <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                    <div></div>
                    <div></div>
                    <Field
                        component={Checkbox}
                        name={`${accessor}.buildingSurfaceAreaOverride.enabled`}
                        label="Override Building Enclosure Area"
                        large
                        className={classes.bottomCheckBox}
                        type="checkbox"
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.buildingSurfaceAreaOverride.totalArea`}
                        label="Total Override Area"
                        placeholder={0.0}
                        disabled={!buildingSurfaceAreaOverrideEnabled || !canBeEditedFromParametric}
                        decimals={2}
                        change={change}
                        units={{
                            base: getBaseUnits("commonAreaWalls", modelUnits),
                            options: getUnitOptions("area"),
                            selected: buildingSurfaceAreaOverrideTotalAreaUnits,
                            unitType: "area",
                            accessor: `${accessor}.buildingSurfaceAreaOverride.totalArea_u`,
                        }}
                        info="Enter the total surface area of the building enclosure. This will override the calculated area based on the components and common areas."
                    />
                </InputRow>
                <h3 style={{ marginBottom: "1rem", marginTop: "2.25rem" }}>Exhaust Devices Test</h3>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={Select}
                        name={`${accessor}.exhaustDevicesTest`}
                        options={[
                            {
                                label: "Not applicable",
                                value: {
                                    id: 0,
                                    result: 0,
                                },
                            },
                            {
                                label: "Not possible to perform test",
                                value: {
                                    id: 1,
                                    result: 0,
                                },
                            },
                            {
                                label: "Test result",
                                value: {
                                    id: 2,
                                    result: exhaustTestResult,
                                },
                            },
                        ]}
                        label="Depressurization Test Status"
                        placeholder="Choose Test Status"
                        validate={depresStatusValidation}
                        onChange={() => {
                            change(`${accessor}.exhaustDevicesTest.result`, 0);
                        }}
                        disabled={!canBeEditedFromParametric}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.exhaustDevicesTest.result`}
                        label="Test Result"
                        placeholder={0.0}
                        disabled={exhaustTestId !== 2 || !canBeEditedFromParametric}
                        validate={depresResultValidation}
                        setValue={exhaustTestResult}
                        setTouched={true}
                        decimals={getDecimalPlaces("depressurizationTestResult")}
                        change={change}
                        units={{
                            base: getBaseUnits("depressurizationTestResult", modelUnits),
                            options: getUnitOptions("pressure"),
                            selected: exhaustDeviceTestUnits,
                            unitType: "pressure",
                            accessor: `${accessor}.exhaustDevicesTest_u`,
                        }}
                    />
                </InputRow>
                {!isUpgrade && (
                    <>
                        <Accordion
                            className={sharedClasses.accordion}
                            // heading={<span className={sharedClasses.heading}>Other Factors</span>}
                            heading={
                                <div className={sharedClasses.accordionIconHeading}>
                                    Other Factors <NonH2kParam />
                                </div>
                            }
                            large
                            forceOpen={contentOpen}
                        >
                            <p>
                                <strong>
                                    For the purposes of EnerGuide compliance, the values in this section should not be
                                    altered.
                                </strong>
                            </p>
                            <h3 style={{ marginBottom: "1rem", marginTop: "1rem" }}>Window Tightness</h3>
                            <InputRow gridTemplate="31.85% 23.25%">
                                <Field
                                    component={Select}
                                    name={`model.modelData.specifications.windowTightness`}
                                    options={[
                                        ...getOptions({ fieldKey: "windowAirTightness" }),
                                        {
                                            label: "User Specified",
                                            value: {
                                                id: 4,
                                                value: windowTightnessValue, //need to get rValue for this to match
                                            },
                                        },
                                    ]}
                                    label="Window Airtightness"
                                    placeholder="Choose Window Airtightness"
                                    validate={windowTightnessValidation}
                                    info={
                                        "The airtightness entered here is used solely for the calculation of window ER values for documentation purposes only and is not used in heat loss calculations."
                                    }
                                    disabled={!canBeEditedFromParametric}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.specifications.windowTightness.value`}
                                    label="Value"
                                    placeholder={0.0}
                                    disabled={windowTightnessId !== 4 || !canBeEditedFromParametric}
                                    validate={windowTightnessValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("windowAirTightness")}
                                    change={change}
                                    setValue={windowTightnessId !== 4 ? windowTightnessValue : null}
                                    units={{
                                        base: getBaseUnits("windowAirTightness", modelUnits),
                                        options: getUnitOptions("windowTightness"),
                                        selected: ["L/s·m²"],
                                        unitType: "windowTightness",
                                        accessor: `model.modelData.specifications.windowTightness_u`,
                                    }}
                                />
                            </InputRow>
                            <h4 style={{ marginBottom: "1rem", marginTop: "1rem" }}>Local Shielding</h4>
                            <InputRow gridTemplate="1fr 1fr 1fr">
                                <Field
                                    component={Select}
                                    name={`model.modelData.airTightness.localShieldingWalls`}
                                    options={getOptions({ fieldKey: "localShieldingWalls" })}
                                    label="Walls"
                                    placeholder="Choose Wall Shielding"
                                    validate={localShieldingWallsValidation}
                                    nonH2kParam="f280Helpful"
                                    disabled={!canBeEditedFromParametric}
                                />
                                <Field
                                    component={Select}
                                    name={`model.modelData.airTightness.localShieldingFlue`}
                                    options={getOptions({ fieldKey: "localShieldingFlue" })}
                                    label="Flue"
                                    placeholder="Choose Flue Shielding"
                                    validate={localShieldingFlueValidation}
                                    nonH2kParam="f280Helpful"
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                            <h4>Building Site</h4>
                            <InputRow gridTemplate="1fr 1fr 1fr">
                                <Field
                                    component={Select}
                                    name={`model.modelData.airTightness.buildingSiteTerrain`}
                                    options={getOptions({ fieldKey: "buildingSiteTerrain" })}
                                    label="Terrain"
                                    placeholder="Choose Site Terrain"
                                    validate={siteTerrainValidation}
                                    nonH2kParam="f280Helpful"
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                            <h4>Weather Station</h4>
                            <InputRow gridTemplate="1fr 1fr 1fr">
                                <Field
                                    component={Select}
                                    name={`model.modelData.airTightness.other.weatherStationTerrain`}
                                    options={getOptions({ fieldKey: "weatherStationTerrain" })}
                                    label="Terrain"
                                    placeholder="Choose Weather Station Terrain"
                                    validate={weatherTerrainValidation}
                                    disabled={!canBeEditedFromParametric}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.airTightness.other.anemometerHeight`}
                                    label="Anemometer Height"
                                    placeholder="0.00"
                                    validate={anemometerValidation}
                                    decimals={getDecimalPlaces("weatherStationAnemometer")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("weatherStationAnemometer", modelUnits),
                                        options: getUnitOptions("length"),
                                        selected: anemometerUnits,
                                        unitType: "length",
                                        accessor: `model.modelData.airTightness.other.anemometerHeight_u`,
                                    }}
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                            <h4>Leakage Factors</h4>
                            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                                <Field
                                    component={Input}
                                    type="number"
                                    name={`model.modelData.airTightness.other.leakageFractions.ceilings`}
                                    label="Ceilings"
                                    placeholder={0.0}
                                    validate={ceilingLeakageValidation}
                                    decimals={getDecimalPlaces("leakageFractionsCeilings")}
                                    disabled={isDefaultLeakage || !canBeEditedFromParametric}
                                    setValue={isDefaultLeakage ? 0.2 : null}
                                />
                                <Field
                                    component={Input}
                                    type="number"
                                    name={`model.modelData.airTightness.other.leakageFractions.walls`}
                                    label="Walls"
                                    placeholder={0.0}
                                    validate={wallsLeakageValidation}
                                    decimals={getDecimalPlaces("leakageFractionsWalls")}
                                    disabled={isDefaultLeakage || !canBeEditedFromParametric}
                                    setValue={isDefaultLeakage ? 0.65 : null}
                                />
                                <Field
                                    component={Input}
                                    type="number"
                                    name={`model.modelData.airTightness.other.leakageFractions.floors`}
                                    label="Floors"
                                    placeholder={0.0}
                                    validate={floorsLeakageValidation}
                                    decimals={getDecimalPlaces("leakageFractionsFloors")}
                                    disabled={isDefaultLeakage || !canBeEditedFromParametric}
                                    setValue={isDefaultLeakage ? 0.15 : null}
                                />
                                <Field
                                    component={Checkbox}
                                    name={`model.modelData.airTightness.other.leakageFractions.useDefaults`}
                                    label="Use Defaults"
                                    large
                                    className={classes.bottomCheckBox}
                                    type="checkbox"
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                        </Accordion>
                        <Accordion
                            className={sharedClasses.accordion}
                            bodyClassName={sharedClasses.testDataBody}
                            heading={<span className={sharedClasses.heading}>Air Leakage Test Data</span>}
                            large
                            forceClose={!isAirTestEnabled}
                            disabled={!isAirTestEnabled || !canBeEditedFromParametric}
                        >
                            <TestData accessor={accessor} change={change} history={history} />
                        </Accordion>
                        <Accordion
                            className={sharedClasses.accordion}
                            heading={<span className={sharedClasses.heading}>Areas of Common Surfaces</span>}
                            large
                            disabled={isDetached || !canBeEditedFromParametric}
                            forceOpen={contentOpen}
                        >
                            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.airTightness.commonSurfaces.floors`}
                                    label="Floors"
                                    placeholder={0.0}
                                    disabled={isDetached || !canBeEditedFromParametric}
                                    validate={commonFloorsValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("commonAreaFloors")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("commonAreaFloors", modelUnits),
                                        options: getUnitOptions("area"),
                                        selected: commonFloorsUnits,
                                        unitType: "area",
                                        accessor: `model.modelData.airTightness.commonSurfaces.floors_u`,
                                    }}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.airTightness.commonSurfaces.walls`}
                                    label="Walls"
                                    placeholder={0.0}
                                    disabled={isDetached || !canBeEditedFromParametric}
                                    validate={commonWallsValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("commonAreaWalls")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("commonAreaWalls", modelUnits),
                                        options: getUnitOptions("area"),
                                        selected: commonWallsUnits,
                                        unitType: "area",
                                        accessor: `model.modelData.airTightness.commonSurfaces.walls_u`,
                                    }}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.airTightness.commonSurfaces.ceilings`}
                                    label="Ceilings"
                                    placeholder={0.0}
                                    disabled={isDetached || !canBeEditedFromParametric}
                                    validate={commonCeilingsValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("commonAreaCeilings")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("commonAreaCeilings", modelUnits),
                                        options: getUnitOptions("area"),
                                        selected: commonCeilingsUnits,
                                        unitType: "area",
                                        accessor: `model.modelData.airTightness.commonSurfaces.ceilings_u`,
                                    }}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`model.modelData.airTightness.commonSurfaces.total`}
                                    label="Total"
                                    placeholder={0.0}
                                    disabled={true}
                                    validate={commonTotalValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("commonAreaTotal")}
                                    setValue={totalCommonAreaValue}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("commonAreaTotal", modelUnits),
                                        options: getUnitOptions("area"),
                                        selected: commonTotalUnits,
                                        unitType: "area",
                                        accessor: `model.modelData.airTightness.commonSurfaces.total_u`,
                                    }}
                                />
                            </InputRow>
                        </Accordion>
                    </>
                )}
            </div>
        </>
    );
};

export default Airtightness;
